// Time-format helpers for METAR / TAF / SIGMET / etc.
//
// Aviation primary time format is Zulu (UTC). Pilots also want the
// local time printed alongside so a glance at "1815Z (10:15 AM PST)"
// is immediate. Three helpers cover the common shapes:
//
//   formatZuluWithLocal(d)     -> "DDHHMMZ (h:mm a TZ)"
//   formatTimestampWithLocal(d) -> long form for header rows
//   formatZuluRangeWithLocal(f, t) -> compact validity-window form
//
// All accept Date | epoch ms | epoch s | ISO string | null/undefined
// and return '—' on garbage input.

export type DateLike = Date | number | string | null | undefined;

function toDate(v: DateLike): Date | null {
  if (v == null) return null;
  if (v instanceof Date) return Number.isFinite(v.getTime()) ? v : null;
  if (typeof v === 'number' && Number.isFinite(v)) {
    // Heuristic: NWS APIs ship epoch SECONDS (10-digit). JS ms is 13-digit.
    // Anything < 10^11 we treat as seconds and upconvert.
    const ms = v < 1e11 ? v * 1000 : v;
    const d = new Date(ms);
    return Number.isFinite(d.getTime()) ? d : null;
  }
  if (typeof v === 'string' && v) {
    const d = new Date(v);
    return Number.isFinite(d.getTime()) ? d : null;
  }
  return null;
}

function pad2(n: number): string {
  return n < 10 ? '0' + n : String(n);
}

// ── 24-hour vs 12-hour clock preference ────────────────────────────────
//
// Single source of truth for the user's clock-format choice. Lives at
// module scope rather than in uiStore so non-React modules (instruments,
// taf, alert text formatters, audit-event payload renderers, etc.)
// can read it without spinning up a zustand subscription.
//
// uiStore calls setClock24hPref() in two places:
//   1. On boot, right after PERSISTED_PREFS hydrates.
//   2. Inside setClock24h() so the toggle takes effect instantly.
//
// Default is true — the primary user is a pilot and METAR / TAF /
// ATIS / ATC are all 24-hour; AM/PM in the cockpit is a mental flip
// nobody asked for.

let _clock24h = true;

/** Called from uiStore. Don't call from anywhere else — read state
 *  via the toLocaleString helpers below. */
export function setClock24hPref(v: boolean): void {
  _clock24h = v;
}

/** Read the current clock preference. Exposed mostly for charts that
 *  build their own time labels (hand-rolled SVG axes). */
export function getClock24hPref(): boolean {
  return _clock24h;
}

/** Format options for an Intl.DateTimeFormat call that respects the
 *  user's 24h/12h choice. Pass the result as the second arg to
 *  toLocaleTimeString / new Intl.DateTimeFormat. */
export function clockTimeFormatOptions(): Intl.DateTimeFormatOptions {
  // hour12 is the only knob — hourCycle is incompletely-supported on
  // older Safari. hour12: false reliably yields 'HH:MM'; hour12: true
  // yields 'h:MM AM/PM'.
  return _clock24h
    ? { hour: '2-digit', minute: '2-digit', hour12: false }
    : { hour: 'numeric', minute: '2-digit', hour12: true };
}

/** Format a Date / epoch / ISO as time-of-day in the user's preferred
 *  clock WITH the short timezone abbreviation appended, e.g.
 *  '18:15 PST' or '6:15 PM PST'. Returns '—' on garbage input.
 *
 *  Use this in place of `toLocaleTimeString([], { hour: 'numeric',
 *  minute: '2-digit' })`. Boss req 2026-05-13: every displayed time
 *  must show the timezone so a glance in the cockpit is unambiguous.
 */
export function formatLocalTime(v: DateLike): string {
  const d = toDate(v);
  if (!d) return '—';
  let t: string;
  try {
    t = new Intl.DateTimeFormat(undefined, clockTimeFormatOptions()).format(d);
  } catch {
    t = _clock24h
      ? `${pad2(d.getHours())}:${pad2(d.getMinutes())}`
      : d.toLocaleTimeString();
  }
  const tz = tzShort(d);
  return tz ? `${t} ${tz}` : t;
}

/** Bare time-of-day WITHOUT timezone — used when the timezone is
 *  already labeled elsewhere on the same line (e.g. inside
 *  formatZuluWithLocal's parenthesized local-time chunk). Rarely
 *  needed; prefer formatLocalTime for normal display. */
export function formatLocalTimeBare(v: DateLike): string {
  const d = toDate(v);
  if (!d) return '—';
  try {
    return new Intl.DateTimeFormat(undefined, clockTimeFormatOptions()).format(d);
  } catch {
    return _clock24h
      ? `${pad2(d.getHours())}:${pad2(d.getMinutes())}`
      : d.toLocaleTimeString();
  }
}

/** Short timezone abbreviation for the browser's locale, e.g. 'PST'
 *  / 'EDT' / 'GMT+9'. Uses Intl.DateTimeFormat with timeZoneName
 *  'short'; if the browser falls back to GMT offsets that's still
 *  readable. */
function tzShort(d: Date): string {
  try {
    const parts = new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      timeZoneName: 'short',
    }).formatToParts(d);
    const tz = parts.find((p) => p.type === 'timeZoneName')?.value;
    return tz || '';
  } catch {
    return '';
  }
}

/** Local time in the user's browser zone. Format follows the user's
 *  24h/12h preference — see clockTimeFormatOptions(). */
function localTime(d: Date): string {
  try {
    return new Intl.DateTimeFormat(undefined, clockTimeFormatOptions()).format(d);
  } catch {
    return _clock24h
      ? `${pad2(d.getHours())}:${pad2(d.getMinutes())}`
      : d.toLocaleTimeString();
  }
}

/** Zulu time the way pilots read aviation reports:
 *  'DDHHMMZ (h:mm AM/PM TZ)' — e.g. '10/1815Z (10:15 AM PST)'.
 *  The day-of-month prefix matches METAR / TAF convention. */
export function formatZuluWithLocal(v: DateLike): string {
  const d = toDate(v);
  if (!d) return '—';
  const dd = pad2(d.getUTCDate());
  const hh = pad2(d.getUTCHours());
  const mm = pad2(d.getUTCMinutes());
  const zulu = `${dd}/${hh}${mm}Z`;
  const tz = tzShort(d);
  const local = localTime(d);
  return tz ? `${zulu} (${local} ${tz})` : `${zulu} (${local})`;
}

/** Long-form timestamp for header rows in detail cards. Example:
 *  '2026-05-10 18:15Z · Sun, May 10 10:15 AM PST'. */
export function formatTimestampWithLocal(v: DateLike): string {
  const d = toDate(v);
  if (!d) return '—';
  const ymd = `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
  const hm = `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;
  const zulu = `${ymd} ${hm}Z`;
  const tz = tzShort(d);
  let local: string;
  try {
    local = new Intl.DateTimeFormat(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      ...clockTimeFormatOptions(),
    }).format(d);
  } catch {
    local = d.toString();
  }
  return tz ? `${zulu} · ${local} ${tz}` : `${zulu} · ${local}`;
}

/** Range form used by TAF validity windows. Example:
 *  '10/1800Z → 11/1800Z (11:00 AM → 11:00 AM PST)'. Compact enough
 *  for a single row. */
export function formatZuluRangeWithLocal(
  fromV: DateLike,
  toV: DateLike,
): string {
  const from = toDate(fromV);
  const to = toDate(toV);
  if (!from && !to) return '—';
  const fromZ = from ? `${pad2(from.getUTCDate())}/${pad2(from.getUTCHours())}${pad2(from.getUTCMinutes())}Z` : '—';
  const toZ = to ? `${pad2(to.getUTCDate())}/${pad2(to.getUTCHours())}${pad2(to.getUTCMinutes())}Z` : '—';
  const fromLocal = from ? localTime(from) : '—';
  const toLocal = to ? localTime(to) : '—';
  const tz = to ? tzShort(to) : (from ? tzShort(from) : '');
  const localPart = tz
    ? `${fromLocal} → ${toLocal} ${tz}`
    : `${fromLocal} → ${toLocal}`;
  return `${fromZ} → ${toZ} (${localPart})`;
}
